
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, TextField, Box, Typography, Alert, Paper, Divider, Snackbar } from '@mui/material';
import { RootState } from '../../store/store';
import { updateUserProfile, changeUserPassword } from '../../services/authServices';
import { authActions } from '../../store/reducers/authReducer';

const MyAccount = () => {
    const user = useSelector((state: RootState) => state.auth.user) as any;
    const dispatch = useDispatch();

    const [profileData, setProfileData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        user_id: ''
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        if (user) {
            setProfileData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                user_id: user.user_id || ''
            });
        }
    }, [user]);

    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const validateProfile = () => {
        if (!profileData.first_name.trim()) return "First name is required.";
        if (!profileData.last_name.trim()) return "Last name is required.";
        return null;
    };

    const validatePassword = () => {
        const { newPassword, confirmNewPassword } = passwordData;

        if (newPassword.length < 8) return "Password must be at least 8 characters long.";
        if (/\s/.test(newPassword)) return "Password must not contain spaces.";
        if (!(/[A-Z]/.test(newPassword))) return "Password must contain at least one uppercase letter.";
        if (!(/[a-z]/.test(newPassword))) return "Password must contain at least one lowercase letter.";
        if (!(/[0-9]/.test(newPassword))) return "Password must contain at least one number.";
        if (!(/[^A-Za-z0-9]/.test(newPassword))) return "Password must contain at least one special character.";

        if (newPassword !== confirmNewPassword) return "New passwords do not match.";

        return null;
    };

    const onUpdateProfile = async () => {
        const error = validateProfile();
        if (error) {
            setProfileMessage({ type: 'error', text: error });
            return;
        }

        try {
            const res = await updateUserProfile(profileData);
            dispatch(authActions.setUserProflie({ ...user, ...profileData }));
            setProfileMessage({ type: 'success', text: "Profile updated successfully." });
        } catch (err: any) {
            setProfileMessage({ type: 'error', text: err?.message || "Failed to update profile." });
        }
    };

    const onChangePassword = async () => {
        const error = validatePassword();
        if (error) {
            setPasswordMessage({ type: 'error', text: error });
            return;
        }

        try {
            await changeUserPassword({
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            setPasswordMessage({ type: 'success', text: "Password changed successfully." });
            setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
        } catch (err: any) {
            setPasswordMessage({ type: 'error', text: err?.error || "Failed to change password." });
        }
    };

    return (
        <div className="p-6">
            <Typography variant="h4" gutterBottom>My Account</Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 600 }}>
                {/* Profile Section */}
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>Personal Details</Typography>
                    <Divider sx={{ mb: 2 }} />

                    {profileMessage && (
                        <Alert severity={profileMessage.type} sx={{ mb: 2 }}>{profileMessage.text}</Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="First Name"
                            name="first_name"
                            value={profileData.first_name}
                            onChange={handleProfileChange}
                            fullWidth
                        />
                        <TextField
                            label="Last Name"
                            name="last_name"
                            value={profileData.last_name}
                            onChange={handleProfileChange}
                            fullWidth
                        />
                        <TextField
                            label="Email"
                            value={profileData.email}
                            disabled
                            fullWidth
                        />
                        <Button variant="contained" onClick={onUpdateProfile} sx={{ alignSelf: 'flex-start' }}>
                            Update Profile
                        </Button>
                    </Box>
                </Paper>

                {/* Password Section */}
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>Change Password</Typography>
                    <Divider sx={{ mb: 2 }} />

                    {passwordMessage && (
                        <Alert severity={passwordMessage.type} sx={{ mb: 2 }}>{passwordMessage.text}</Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Current Password"
                            name="currentPassword"
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            fullWidth
                        />
                        <TextField
                            label="New Password"
                            name="newPassword"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            helperText="Min 8 chars, 1 Upper, 1 Lower, 1 Number, 1 Special, No Spaces."
                            fullWidth
                        />
                        <TextField
                            label="Confirm New Password"
                            name="confirmNewPassword"
                            type="password"
                            value={passwordData.confirmNewPassword}
                            onChange={handlePasswordChange}
                            fullWidth
                        />
                        <Button variant="contained" color="secondary" onClick={onChangePassword} sx={{ alignSelf: 'flex-start' }}>
                            Change Password
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </div>
    );
};

export default MyAccount;
